class CartReservation extends HTMLElement {
  constructor() {
    super();
    this.interval = null;
    this.runningElement = null;
    this.endedElement = null;
    this.timerElement = null;
    this.container = null;
    this.expiresAt = null;
    this.isEmptyingCart = false;
  }

  connectedCallback() {
    this.runningElement = this.querySelector('[data-ref="running"]');
    this.endedElement = this.querySelector('[data-ref="ended"]');
    this.timerElement = this.querySelector('[data-ref="timer"]');
    this.container = this.closest('.cart-reservation');

    if (!this.container || !this.runningElement || !this.endedElement || !this.timerElement) return;

    const cartItemCount = Number.parseInt(this.dataset.cartItemCount || '0', 10);
    const isPreviewMode = this.container.hasAttribute('data-visual-preview-mode');

    if (cartItemCount <= 0) {
      this.#clearReservation(true);

      if (!isPreviewMode) {
        this.container.classList.add('hidden');
        return;
      }

      this.#showRunning();
      this.timerElement.textContent = this.#formatDuration();
      return;
    }

    this.container.classList.remove('hidden');

    this.expiresAt = this.#getStoredExpiration();
    if (!this.expiresAt) {
      this.expiresAt = Date.now() + this.#getDurationInMs();
      this.#storeExpiration(this.expiresAt);
    }

    this.#updateCountdown();

    if (this.expiresAt > Date.now()) {
      this.interval = window.setInterval(() => this.#updateCountdown(), 1000);
    }
  }

  disconnectedCallback() {
    this.#clearInterval();
  }

  #getStorageKey() {
    const cartToken = this.dataset.cartToken || 'default';
    const cartItemCount = this.dataset.cartItemCount || '0';
    const durationMinutes = this.#getDurationMinutes();
    return `fs-cart-reservation-${cartToken}-${cartItemCount}-${durationMinutes}`;
  }

  #getStorageKeyPrefix() {
    const cartToken = this.dataset.cartToken || 'default';
    return `fs-cart-reservation-${cartToken}-`;
  }

  #getDurationMinutes() {
    const durationMinutes = Number.parseInt(this.dataset.durationMinutes || '10', 10);
    return Number.isNaN(durationMinutes) || durationMinutes < 1 ? 10 : durationMinutes;
  }

  #getDurationInMs() {
    return this.#getDurationMinutes() * 60 * 1000;
  }

  #formatDuration() {
    const totalSeconds = Math.floor(this.#getDurationInMs() / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  #getStoredExpiration() {
    let storedValue = null;

    try {
      storedValue = localStorage.getItem(this.#getStorageKey());
    } catch {
      return null;
    }

    if (!storedValue) return null;

    const expiration = Number.parseInt(storedValue, 10);
    if (Number.isNaN(expiration)) return null;
    if (expiration <= Date.now()) {
      this.#clearReservation(true);
      return null;
    }

    return expiration;
  }

  #storeExpiration(expiration) {
    try {
      localStorage.setItem(this.#getStorageKey(), `${expiration}`);
    } catch {
      return;
    }
  }

  #clearReservation(clearAllCartReservations = false) {
    try {
      if (clearAllCartReservations) {
        this.#clearStoredReservations();
      } else {
        localStorage.removeItem(this.#getStorageKey());
      }
    } catch {}

    this.#clearInterval();
  }

  #clearStoredReservations() {
    const storageKeyPrefix = this.#getStorageKeyPrefix();

    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      if (key && key.startsWith(storageKeyPrefix)) {
        localStorage.removeItem(key);
      }
    }
  }

  #clearInterval() {
    if (!this.interval) return;
    window.clearInterval(this.interval);
    this.interval = null;
  }

  #updateCountdown() {
    if (!this.expiresAt) return;

    const remainingMs = this.expiresAt - Date.now();
    if (remainingMs <= 0) {
      this.#showEnded();
      this.#clearInterval();
      return;
    }

    const totalSeconds = Math.ceil(remainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    this.timerElement.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    this.#showRunning();
  }

  #showRunning() {
    this.runningElement.classList.remove('hidden');
    this.endedElement.classList.add('hidden');
  }

  #showEnded() {
    if (this.dataset.endedStyle === 'empty_cart') {
      this.#emptyCart();
      return;
    }

    if (this.dataset.endedStyle === 'hide_block') {
      this.container.classList.add('hidden');
      return;
    }

    this.runningElement.classList.add('hidden');
    this.endedElement.classList.remove('hidden');
  }

  async #emptyCart() {
    if (this.isEmptyingCart) return;

    this.isEmptyingCart = true;
    this.runningElement.classList.add('hidden');
    this.endedElement.classList.add('hidden');
    this.container.classList.add('hidden');

    const cartDrawer = document.querySelector('cart-drawer');
    const cartDrawerSectionId = cartDrawer ? cartDrawer.dataset.sectionId : null;
    const body = {};

    if (cartDrawerSectionId) {
      body.sections = cartDrawerSectionId;
      body.sections_url = window.location.pathname;
    }

    try {
      const response = await fetch(`${Theme.routes.cart_url}/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error('Could not empty cart');

      const cart = await response.json();

      this.#clearReservation(true);

      if (window.location.pathname.includes('/cart')) {
        window.location.reload();
        return;
      }

      if (cartDrawer && cartDrawerSectionId && cart.sections && cart.sections[cartDrawerSectionId]) {
        cartDrawer.renderCartContents({
          itemCount: cart.item_count,
          sections: cart.sections,
        });
      }

      for (const cartIcon of document.querySelectorAll('cart-icon [data-ref="cart-count"]')) {
        cartIcon.textContent = cart.item_count;
      }
    } catch (error) {
      console.error(error);
      this.isEmptyingCart = false;
    }
  }
}

if (!customElements.get('cart-reservation')) {
  customElements.define('cart-reservation', CartReservation);
}
